/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import {
  type IPollingService,
  type RepeatFunction,
  type StopPollingFunction,
  type PollingOptions,
  type ActivePoll
} from '../types/polling'

/**
 * PollingService - React TypeScript equivalent of AngularJS pollingService
 *
 * Provides polling functionality including:
 * - Configurable interval polling operations
 * - Auth service integration for dynamic intervals
 * - Cleanup and management of multiple polling operations
 * - Error handling and iteration tracking
 */
class PollingService implements IPollingService {
  private static instance: PollingService
  private defaultInterval: number = 20000 // 20 seconds default
  private activePolls: Map<string, ActivePoll> = new Map()
  private pollIdCounter: number = 0

  /**
   * Get singleton instance of PollingService
   */
  public static getInstance(): PollingService {
    if (!PollingService.instance) {
      PollingService.instance = new PollingService()
    }
    return PollingService.instance
  }

  /**
   * Start a repeating operation until manually stopped
   * @param repeat - Function to execute repeatedly
   * @param options - Optional configuration
   * @returns Function to stop the polling
   */
  public repeatUntil(repeat: RepeatFunction, options: PollingOptions = {}): StopPollingFunction {
    const pollId = this.generatePollId()
    const interval = this.getPollingInterval(options.interval)

    let iterationCount = 0
    const startTime = Date.now()

    // Create polling function with error handling
    const pollFunction = () => {
      try {
        // Check if we've reached max iterations
        if (options.maxIterations && iterationCount >= options.maxIterations) {
          this.stopPoll(pollId)
          return
        }

        iterationCount++

        // Execute the repeat function (handle both sync and async)
        const result = repeat()

        // Update iteration count in active polls
        const activePoll = this.activePolls.get(pollId)
        if (activePoll) {
          activePoll.iterationCount = iterationCount
        }

        // Handle async results
        if (result && typeof result.then === 'function') {
          result.catch((error: Error | unknown) => {
            const errorObj = error instanceof Error ? error : new Error(String(error))

            if (options.onError) {
              options.onError(errorObj)
            } else {
              console.error('Polling operation failed:', errorObj)
            }
          })
        }
      } catch (error) {
        const errorObj = error instanceof Error ? error : new Error(String(error))

        if (options.onError) {
          options.onError(errorObj)
        } else {
          console.error('Polling operation failed:', errorObj)
        }

        // Optionally stop polling on error
        if (options.maxIterations === 1) {
          this.stopPoll(pollId)
        }
      }
    }

    // Execute immediately if requested
    if (options.immediate) {
      pollFunction()
    }

    // Set up interval
    const intervalId = setInterval(pollFunction, interval)

    // Track the active poll
    const activePoll: ActivePoll = {
      id: pollId,
      intervalId,
      repeatFn: repeat,
      options,
      startTime,
      iterationCount
    }

    this.activePolls.set(pollId, activePoll)

    // Return stop function
    return () => this.stopPoll(pollId)
  }

  /**
   * Get the default polling interval
   * @returns Polling interval in milliseconds
   */
  public getDefaultInterval(): number {
    return this.defaultInterval
  }

  /**
   * Set the default polling interval
   * @param interval - Interval in milliseconds
   */
  public setDefaultInterval(interval: number): void {
    if (interval > 0) {
      this.defaultInterval = interval
    } else {
      console.warn('Polling interval must be positive')
    }
  }

  /**
   * Stop all active polling operations
   */
  public stopAll(): void {
    this.activePolls.forEach((_, pollId) => {
      this.stopPoll(pollId)
    })
  }

  /**
   * Get the number of active polling operations
   * @returns Number of active polls
   */
  public getActiveCount(): number {
    return this.activePolls.size
  }

  /**
   * Get polling interval with fallback hierarchy
   * @param optionsInterval - Interval from options
   * @returns Final polling interval to use
   */
  private getPollingInterval(optionsInterval?: number): number {
    // Priority: options > auth service > default
    if (optionsInterval && optionsInterval > 0) {
      return optionsInterval
    }

    // Try to get from auth service (equivalent to Auth.pollingInterval)
    const authInterval = this.getAuthPollingInterval()
    if (authInterval && authInterval > 0) {
      return authInterval
    }

    return this.defaultInterval
  }

  /**
   * Get polling interval from auth service
   * @returns Auth service polling interval or null
   */
  private getAuthPollingInterval(): number | null {
    try {
      // This would integrate with the actual auth service
      // For now, we'll check for a global auth object or service
      const globalWindow = window as unknown as Record<string, unknown>
      const auth = globalWindow.auth || globalWindow.Auth
      if (auth && typeof (auth as Record<string, unknown>).pollingInterval === 'number') {
        return (auth as Record<string, unknown>).pollingInterval as number
      }

      // Alternative: check for a global polling interval setting
      const config = globalWindow.config || globalWindow.APP_CONFIG
      if (config && typeof (config as Record<string, unknown>).pollingInterval === 'number') {
        return (config as Record<string, unknown>).pollingInterval as number
      }

      return null
    } catch (error) {
      console.warn('Could not retrieve auth polling interval:', error)
      return null
    }
  }

  /**
   * Stop a specific polling operation
   * @param pollId - ID of the poll to stop
   */
  private stopPoll(pollId: string): void {
    const activePoll = this.activePolls.get(pollId)
    if (activePoll) {
      clearInterval(activePoll.intervalId)
      this.activePolls.delete(pollId)
    }
  }

  /**
   * Generate unique poll ID
   * @returns Unique identifier for polling operation
   */
  private generatePollId(): string {
    return `poll_${++this.pollIdCounter}_${Date.now()}`
  }

  /**
   * Get information about a specific poll
   * @param pollId - ID of the poll
   * @returns Poll information or null if not found
   */
  public getPollInfo(pollId: string): ActivePoll | null {
    return this.activePolls.get(pollId) || null
  }

  /**
   * Get information about all active polls
   * @returns Array of active poll information
   */
  public getAllPollsInfo(): ActivePoll[] {
    return Array.from(this.activePolls.values())
  }

  /**
   * Create a one-time delayed execution (like setTimeout but trackable)
   * @param fn - Function to execute
   * @param delay - Delay in milliseconds
   * @returns Function to cancel the delayed execution
   */
  public delayed(fn: RepeatFunction, delay: number): StopPollingFunction {
    return this.repeatUntil(fn, {
      interval: delay,
      maxIterations: 1,
      immediate: false
    })
  }

  /**
   * Create a simple interval (like setInterval but trackable)
   * @param fn - Function to execute
   * @param interval - Interval in milliseconds
   * @returns Function to stop the interval
   */
  public interval(fn: RepeatFunction, interval: number): StopPollingFunction {
    return this.repeatUntil(fn, {
      interval,
      immediate: false
    })
  }
}

// Export singleton instance
export const pollingService = PollingService.getInstance()
export default pollingService
